import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  Paper,
  TextField,
  Typography,
  Button,
  Divider,
  Fade,
  useTheme,
  useMediaQuery,
  Chip,
  Alert,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import sidebarlogo from "../../assets/img/sidebar_logo.png";
import BudgieLogoGreen from "../../assets/img/BudgieLogoGreen.png";
import profileImage from "../../assets/img/profile.png";
import { useSendChatMessageMutation } from "@/api/services/chatBotNew/ChatBotApi";
import ReactMarkdown from "react-markdown";

const ChatBotNew = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setsessionId] = useState(null);

  const [messages, setMessages] = useState([
    {
      from: "bot",
      text: "Hello! How can I help you today?",
      timestamp: new Date(),
    },
  ]);

  const [input, setInput] = useState("");
  const [showAutoMessage, setShowAutoMessage] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [validationError, setValidationError] = useState("");
  const [showValidationError, setShowValidationError] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const [
    sendChatMessage,
    { data: sendChatMessageData, isLoading: isRTKLoading },
  ] = useSendChatMessageMutation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const TypingIndicator = () => (
    <Box
      sx={{
        alignSelf: "flex-start",
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 1,
        maxWidth: isMobile ? "85%" : "80%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        <Box
          sx={{
            bgcolor: "grey.200",
            color: "black",
            px: isMobile ? 1.5 : 2,
            py: isMobile ? 0.8 : 1,
            borderRadius: 2,
            fontSize: isMobile ? "0.875rem" : "1rem",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.2 }}>
            <img
              src={BudgieLogoGreen}
              alt="Budgie Logo"
              style={{
                width: isMobile ? "24px" : "22px",
                height: isMobile ? "24px" : "22px",
              }}
            />
            <Typography variant="body2">thinking</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.3 }}>
            <Box
              sx={{
                width: 4,
                height: 4,
                borderRadius: "50%",
                bgcolor: "grey.600",
                animation: "typing 1.4s infinite ease-in-out",
                animationDelay: "0s",
                "@keyframes typing": {
                  "0%, 80%, 100%": { opacity: 0.3 },
                  "40%": { opacity: 1 },
                },
              }}
            />
            <Box
              sx={{
                width: 4,
                height: 4,
                borderRadius: "50%",
                bgcolor: "grey.600",
                animation: "typing 1.4s infinite ease-in-out",
                animationDelay: "0.2s",
                "@keyframes typing": {
                  "0%, 80%, 100%": { opacity: 0.3 },
                  "40%": { opacity: 1 },
                },
              }}
            />
            <Box
              sx={{
                width: 4,
                height: 4,
                borderRadius: "50%",
                bgcolor: "grey.600",
                animation: "typing 1.4s infinite ease-in-out",
                animationDelay: "0.4s",
                "@keyframes typing": {
                  "0%, 80%, 100%": { opacity: 0.3 },
                  "40%": { opacity: 1 },
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const localStorageData = JSON.parse(localStorage.getItem("loginDetails"));
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const maxFileSize = 15 * 1024 * 1024;
    const maxFiles = 5;

    if (selectedFiles.length + files.length > maxFiles) {
      setValidationError(
        `Too many files selected. Maximum ${maxFiles} files allowed.`
      );
      setShowValidationError(true);
      event.target.value = "";
      return;
    }

    const oversizedFiles = files.filter((file) => file.size > maxFileSize);
    if (oversizedFiles.length > 0) {
      setValidationError(
        `File(s) too large: ${oversizedFiles
          .map((f) => f.name)
          .join(", ")}. Maximum file size is 15MB each.`
      );
      setShowValidationError(true);
      event.target.value = "";
      return;
    }

    setSelectedFiles((prev) => [...prev, ...files]);
    event.target.value = "";
  };

  const handleFileRemove = (indexToRemove) => {
    setSelectedFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleRemoveAllFiles = () => {
    setSelectedFiles([]);
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleCloseValidationError = () => {
    setShowValidationError(false);
  };

  const handleSend = async () => {
    if (isTyping) {
      setValidationError("Please wait for the current response to complete.");
      setShowValidationError(true);
      return;
    }
    if (!input.trim()) {
      setValidationError("Please enter a message before sending.");
      setShowValidationError(true);
      return;
    }
    if (!localStorageData?.userDetails?.empId) {
      setValidationError("User authentication error. Please log in again.");
      setShowValidationError(true);
      return;
    }
    const userMessage = input.trim();
    const hasFiles = selectedFiles.length > 0;
    const displayMessage = hasFiles
      ? `${userMessage}${userMessage ? "\n" : ""}📎 ${
          selectedFiles.length
        } file(s): ${selectedFiles.map((f) => f.name).join(", ")}`
      : userMessage;

    setMessages([
      ...messages,
      { from: "user", text: displayMessage, timestamp: new Date() },
    ]);
    setInput("");
    setIsTyping(true);

    try {
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => {
        abortController.abort();
      }, 40000);

      const formData = new FormData();
      formData.append("user_id", localStorageData.userDetails.empId);
      if (sessionId !== null) {
        formData.append("session_id", sessionId);
      }
      formData.append("query", userMessage);
      formData.append("feature", "budgie");

      selectedFiles.forEach((file, index) => {
        formData.append("files", file);
      });
      const [fetchResponse, rtkResponse] = await Promise.allSettled([
        fetch("https://chatbotinsightsdev.ckdigital.in/api/chat", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorageData.token}`,
          },
          body: formData,
          signal: abortController.signal,
        }),
        sendChatMessage(formData).unwrap(),
      ]);

      clearTimeout(timeoutId);
      let fetchData = null;
      if (fetchResponse.status === "fulfilled") {
        if (fetchResponse.value.ok) {
          const text = await fetchResponse.value.text();
          try {
            // Handle SSE format: "data: {...}" or multiple "data: {...}" lines
            const lines = text
              .split("\n")
              .filter((line) => line.trim().startsWith("data:"));
            const lastDataLine = lines[lines.length - 1];
            if (lastDataLine) {
              const jsonStr = lastDataLine.replace(/^data:\s*/, "").trim();
              fetchData = jsonStr ? JSON.parse(jsonStr) : null;
            } else {
              fetchData = text ? JSON.parse(text) : null;
            }
          } catch (parseError) {
            console.error("Failed to parse chatbot response:", parseError);
            fetchData = { error: "Invalid response format from server." };
          }
          if (fetchData?.session_id) {
            setsessionId(fetchData.session_id);
          }
        } else {
          try {
            const errorData = await fetchResponse.value.json();
            fetchData = {
              error:
                errorData.error ||
                `HTTP ${fetchResponse.value.status}: ${fetchResponse.value.statusText}`,
            };
          } catch {
            fetchData = {
              error: `HTTP ${fetchResponse.value.status}: ${fetchResponse.value.statusText}`,
            };
          }
        }
      }
      let rtkData = null;
      if (rtkResponse.status === "fulfilled") {
        rtkData = rtkResponse.value;
        if (rtkData.session_id) {
          setsessionId(rtkData.session_id);
        }
      } else if (rtkResponse.status === "rejected") {
        const error = rtkResponse.reason;
        rtkData = {
          error: error?.data?.error || error?.message || "Something went wrong",
        };
      }
      const responseData = fetchData;

      if (responseData) {
        if (responseData.error) {
          setIsTyping(false);
          setMessages((prev) => [
            ...prev,
            {
              from: "bot",
              text: `Error: ${responseData.error}. Please try again.`,
              timestamp: new Date(),
            },
          ]);
        } else {
          setIsTyping(false);
          setSelectedFiles([]);
          setMessages((prev) => [
            ...prev,
            {
              from: "bot",
              text:
                responseData.response ||
                responseData.message ||
                "I'm here to help!",
              timestamp: new Date(),
            },
          ]);
        }
      } else {
        setIsTyping(false);
        setMessages((prev) => [
          ...prev,
          {
            from: "bot",
            text: "Sorry, I'm having trouble responding right now. Please try again.",
            timestamp: new Date(),
          },
        ]);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setIsTyping(false); 
      let errorMessage =
        "Sorry, I'm having trouble connecting. Please check your internet connection and try again.";

      if (error.name === "AbortError") {
        errorMessage = "Something went wrong Please try again";
      } else if (error.message?.includes("Failed to fetch")) {
        errorMessage =
          "Network error: Unable to connect to the server. Please check your internet connection.";
      } else if (error.status === 400) {
        errorMessage =
          "Invalid request. Please check your message and try again.";
      } else if (error.status === 401) {
        errorMessage = "Authentication error. Please log in again.";
      } else if (error.status === 500) {
        errorMessage = "Server error. Please try again later.";
      }

      setMessages((prev) => [
        ...prev,
        {
          from: "bot",
          text: errorMessage,
          timestamp: new Date(),
        },
      ]);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    if (!open) {
      setShowAutoMessage(true);
      const hideTimer = setTimeout(() => {
        setShowAutoMessage(false);
      }, 5000);

      return () => {
        clearTimeout(hideTimer);
      };
    }
  }, [open]);
  useEffect(() => {
    if (showValidationError) {
      const hideTimer = setTimeout(() => {
        setShowValidationError(false);
      }, 4000);

      return () => {
        clearTimeout(hideTimer);
      };
    }
  }, [showValidationError]);

  return (
    <>
      {!open && (
        <Box
          sx={{
            position: "fixed",
            bottom: isMobile ? 15 : 20,
            right: isMobile ? 15 : 20,
            zIndex: 1000,
          }}
        >
          <IconButton
            onClick={() => setOpen(true)}
            sx={{
              width: isMobile ? 50 : 45,
              height: isMobile ? 50 : 45,
              bgcolor: "primary.main",
              color: "white",
              "&:hover": { bgcolor: "primary.dark" },
            }}
          >
            <img
              src={sidebarlogo}
              alt="Sidebar Logo"
              width={isMobile ? "40px" : "35px"}
            />
          </IconButton>

          <Fade in={showAutoMessage} timeout={500}>
            <Paper
              elevation={3}
              sx={{
                position: "absolute",
                bottom: isMobile ? 60 : 55,
                right: 0,
                p: isMobile ? 1 : 1.5,
                bgcolor: "primary.main",
                color: "white",
                borderRadius: 2,
                minWidth: isMobile ? 180 : 200,
                maxWidth: isMobile ? 220 : 250,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: "100%",
                  right: "20px",
                  width: 0,
                  height: 0,
                  borderLeft: "8px solid transparent",
                  borderRight: "8px solid transparent",
                  borderTop: "8px solid",
                  borderTopColor: "primary.main",
                },
              }}
            >
              <Typography
                variant={isMobile ? "caption" : "body2"}
                sx={{ fontWeight: 500 }}
              >
                👋 Hi there! Need help? Click me to start chatting!
              </Typography>
            </Paper>
          </Fade>
        </Box>
      )}

      {open && (
        <Paper
          elevation={4}
          sx={{
            position: "fixed",
            bottom: isMobile ? 0 : 20,
            right: isMobile ? 0 : 20,
            left: isMobile ? 0 : "auto",
            width: isMobile ? "100vw" : isTablet ? 300 : 320,
            height: isMobile ? "100vh" : isTablet ? 400 : 420,
            display: "flex",
            flexDirection: "column",
            zIndex: 1000,
            borderRadius: isMobile ? 0 : 2,
          }}
        >
          <Box
            sx={{
              p: isMobile ? 2 : 1.5,
              bgcolor: "primary.main",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderTopLeftRadius: isMobile ? 0 : 8,
              borderTopRightRadius: isMobile ? 0 : 8,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <img
                src={sidebarlogo}
                alt="Sidebar Logo"
                style={{ width: isMobile ? "45px" : "40px" }}
              />
              <Typography fontWeight="bold" variant={isMobile ? "h6" : "body1"}>
                Budgie Bot
              </Typography>
            </Box>
            <IconButton
              size={isMobile ? "medium" : "small"}
              onClick={() => setOpen(false)}
              sx={{ color: "white" }}
            >
              <CloseIcon fontSize={isMobile ? "medium" : "small"} />
            </IconButton>
          </Box>

          <Divider />

          <Box
            sx={{
              flex: 1,
              p: isMobile ? "16px 5px" : "16px 10px",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: isMobile ? 1 : 1.5,
            }}
          >
            {messages.map((msg, i) => (
              <Box
                key={i}
                sx={{
                  alignSelf: msg.from === "user" ? "flex-end" : "flex-start",
                  display: "flex",
                  flexDirection: msg.from === "user" ? "row-reverse" : "row",
                  alignItems: "flex-start",
                  gap: 1,
                  maxWidth: isMobile ? "85%" : "100%",
                }}
              >
                <Box
                  sx={{
                    padding: 0.5,
                    backgroundColor:
                      msg.from === "user" ? "primary.light" : "grey.300",
                    borderRadius: "50%",
                    width: isMobile ? "25px" : "23px",
                    height: isMobile ? "25px" : "23px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    mt: 0.5,
                  }}
                >
                  {msg.from === "user" ? (
                    localStorageData?.userDetails?.firstName ? (
                      <Typography
                        sx={{
                          color: "white",
                          fontWeight: "bold",
                          fontSize: isMobile ? "12px" : "10px",
                          textTransform: "uppercase",
                        }}
                      >
                        {localStorageData.userDetails.firstName.charAt(0)}
                      </Typography>
                    ) : (
                      <img
                        src={profileImage}
                        alt="User Avatar"
                        style={{
                          width: isMobile ? "20px" : "18px",
                          height: isMobile ? "20px" : "18px",
                          borderRadius: "50%",
                        }}
                      />
                    )
                  ) : (
                    <img
                      src={BudgieLogoGreen}
                      alt="Budgie Logo"
                      style={{
                        width: isMobile ? "20px" : "18px",
                        height: isMobile ? "20px" : "18px",
                      }}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                  }}
                >
                  <Box
                    sx={{
                      bgcolor:
                        msg.from === "user" ? "primary.light" : "grey.200",
                      color: msg.from === "user" ? "white" : "black",
                      px: isMobile ? 1.5 : 2,
                      py: isMobile ? 0.8 : 1,
                      borderRadius: 2,
                      fontSize: isMobile ? "14px" : "12px",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      "& p": {
                        margin: "8px 0px",
                        "&:first-of-type": { marginTop: 0 },
                        "&:last-of-type": { marginBottom: 0 },
                      },
                    }}
                  >
                    <ReactMarkdown>{msg.text}</ReactMarkdown>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: isMobile ? "0.7rem" : "0.75rem",
                      mt: 0.5,
                      alignSelf:
                        msg.from === "user" ? "flex-end" : "flex-start",
                      px: 0.5,
                    }}
                  >
                    {formatTimestamp(msg.timestamp)}
                  </Typography>
                </Box>
              </Box>
            ))}

            {showValidationError && (
              <Box
                sx={{
                  alignSelf: "center",
                  display: "flex",
                  flexDirection: "column",
                  maxWidth: isMobile ? "90%" : "85%",
                  mb: 1,
                }}
              >
                <Alert
                  severity="error"
                  onClose={handleCloseValidationError}
                  sx={{
                    fontSize: isMobile ? "0.875rem" : "1rem",
                    "& .MuiAlert-message": {
                      fontSize: "inherit",
                    },
                  }}
                >
                  {validationError}
                </Alert>
              </Box>
            )}

            {isTyping && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </Box>

          <Divider />

          {selectedFiles.length > 0 && (
            <Box
              sx={{
                p: isMobile ? 1.5 : 1,
                pt: isMobile ? 1 : 0.5,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {selectedFiles.length} file(s) selected
                </Typography>
                <Button
                  size="small"
                  onClick={handleRemoveAllFiles}
                  sx={{ minWidth: "auto", p: 0.5, fontSize: "0.75rem" }}
                >
                  Clear All
                </Button>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  maxHeight: 100,
                  overflowY: "auto",
                }}
              >
                {selectedFiles.map((file, index) => (
                  <Chip
                    key={`${file.name}-${index}`}
                    label={file.name}
                    size="small"
                    onDelete={() => handleFileRemove(index)}
                    deleteIcon={<DeleteIcon />}
                    sx={{
                      maxWidth: 180,
                      "& .MuiChip-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              p: isMobile ? 1.5 : 1,
              gap: isMobile ? 1.5 : 1,
              paddingBottom: isMobile ? 2 : 1,
              paddingTop: selectedFiles.length > 0 ? 0.5 : isMobile ? 1.5 : 1,
            }}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: "none" }}
              accept="*/*"
              multiple
            />

            <TextField
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !isTyping && handleSend()}
              placeholder={"Type your message..."}
              variant="outlined"
              size={isMobile ? "medium" : "small"}
              fullWidth
              // disabled={isTyping}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: isMobile ? "1rem" : "0.875rem",
                },
              }}
            />

            <IconButton
              onClick={handleFileButtonClick}
              color="primary"
              size={isMobile ? "medium" : "small"}
              disabled={isTyping}
              sx={{
                minWidth: isMobile ? 48 : 40,
                minHeight: isMobile ? 48 : 40,
                opacity: isTyping ? 0.5 : 1,
              }}
            >
              <AttachFileIcon fontSize={isMobile ? "medium" : "small"} />
            </IconButton>

            <IconButton
              onClick={handleSend}
              color="primary"
              size={isMobile ? "medium" : "small"}
              disabled={!input.trim() || isTyping}
              sx={{
                minWidth: isMobile ? 48 : 40,
                minHeight: isMobile ? 48 : 40,
                opacity: !input.trim() || isTyping ? 0.5 : 1,
              }}
            >
              <SendIcon fontSize={isMobile ? "medium" : "small"} />
            </IconButton>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default ChatBotNew;
